import React, { useState, useEffect, useCallback, useRef } from 'react'
import ReactDOM from 'react-dom'
import { useDropzone } from 'react-dropzone'
import { Modal } from 'components/ui/Modal'
import { Button } from 'components/ui/Button'
import { Badge } from 'components/ui/Badge'
import { Progress } from 'components/ui/Progress'
import {
  Upload,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  X,
  Copy,
  Info,
  Pause,
  Play
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNotifications } from 'contexts/NotificationContext'
import { uploadsApi, teamsApi, type Team } from 'lib/api'
import { useCVProcessing } from 'contexts/CVProcessingContext'
import { batchPollingService } from 'lib/batchPollingService'
import { EmailImportView } from './EmailImportView'

interface AddCandidatesModalProps {
  isOpen: boolean
  onClose: () => void
  canMassImport: boolean
  onImportComplete?: () => void
  initialTeamId?: number
  lockTeamSelection?: boolean
}

interface BatchStatus {
  batch_id: string
  batch_name: string
  status: string
  progress_percentage: number
  total_files: number
  processed_files: number
  successful_files: number
  failed_files: number
  created_at: string
  started_at: string
  completed_at: string
  task_id: string
  files: Array<{
    file_index: number
    filename: string
    status: string
    error_message?: string
    cv_upload_id: number
  }>
  skipped_files?: Array<{
    filename: string
    file_index: number
    reason: string
    message: string
    duplicate_cv_id?: number
    duplicate_candidate_id?: number
    duplicate_candidate_name?: string
    duplicate_uploaded_at?: string
  }>
}

interface StagedFile {
  filename: string
  status: 'ready' | 'duplicate' | 'error'
  reason?: string
  message?: string
  cv_upload_id?: number
  file_size?: number
  duplicate_candidate_name?: string
  file_index?: number
}

export function AddCandidatesModal({
  isOpen,
  onClose,
  canMassImport,
  onImportComplete,
  initialTeamId,
  lockTeamSelection = false
}: AddCandidatesModalProps) {
  const { t, i18n } = useTranslation()
  const { addNotification } = useNotifications()
  const {
    startProcessing,
    finishProcessing,
    setStatusMessage,
    reportError,
    removeBatch
  } = useCVProcessing()

  const [activeTab, setActiveTab] = useState<'local' | 'email'>('local')
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [selectedTeam, setSelectedTeam] = useState('')
  const [availableTeams, setAvailableTeams] = useState<Team[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [batchStatus, setBatchStatus] = useState<BatchStatus | null>(null)
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null)
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null) // Track active batch ID
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null) // Ref to persist interval across unmounts
  const activeBatchIdRef = useRef<string | null>(null) // Ref to persist batch ID
  const [aiAvailable, setAiAvailable] = useState<boolean | null>(null)
  const [aiMessage, setAiMessage] = useState('')
  const [externalConsentRequired, setExternalConsentRequired] = useState(false)
  const [canGrantExternalConsent, setCanGrantExternalConsent] = useState(false)
  const [grantingConsent, setGrantingConsent] = useState(false)
  const [pendingBatchSummary, setPendingBatchSummary] = useState<{
    batchId: string
    batchName: string
    totalFiles: number
  } | null>(null)
  const [showInfoTooltip, setShowInfoTooltip] = useState(false)
  const infoIconRef = useRef<HTMLButtonElement>(null)
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null)
  const [duplicateError, setDuplicateError] = useState<{
    allDuplicates: boolean
    duplicateCount: number
    duplicateFiles: Array<{ filename: string; duplicate_candidate_name?: string }>
  } | null>(null)
  const [fileHashes, setFileHashes] = useState<Map<string, string>>(new Map()) // filename -> hash
  const [duplicateFilesInList, setDuplicateFilesInList] = useState<Map<string, string[]>>(new Map()) // hash -> filenames[]
  const [localStep, setLocalStep] = useState<'select' | 'review'>('select')
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([])
  const [reviewBatchId, setReviewBatchId] = useState<string | null>(null)
  const [isStaging, setIsStaging] = useState(false)
  const [isStartingImport, setIsStartingImport] = useState(false)
  const [isControllingBatch, setIsControllingBatch] = useState(false)
  const userCancelledRef = useRef(false)

  useEffect(() => {
    if (!isOpen || !canMassImport) return
    loadTeams()
    checkAiAvailability()
    
    // Resume polling for active batch if modal reopens
    if (activeBatchIdRef.current && !pollingIntervalRef.current) {
      startStatusPolling(activeBatchIdRef.current)
    }
  }, [isOpen, canMassImport])

  // Check for active batch on mount and resume polling
  useEffect(() => {
    // Resume polling if there's an active batch
    if (activeBatchIdRef.current && !pollingIntervalRef.current) {
      startStatusPolling(activeBatchIdRef.current)
    }
    
    // Cleanup on unmount - but only if batch is complete
    return () => {
      // Don't clear polling if batch is still active - let it continue in background
      // This allows the indicator to keep updating even when modal is closed
      if (pollingIntervalRef.current && !activeBatchIdRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!isOpen) return
    if (initialTeamId) {
      setSelectedTeam(initialTeamId.toString())
      return
    }

    if (!lockTeamSelection && !initialTeamId && !selectedTeam) {
      setSelectedTeam('')
    }
  }, [initialTeamId, isOpen, lockTeamSelection, selectedTeam])

  // Calculate MD5 hash for a file using Web Crypto API
  const calculateFileHash = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer()
    const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
    return hashHex
  }

  // Check for duplicates within selected files
  const checkDuplicatesInList = async (files: File[]) => {
    const hashMap = new Map<string, string[]>() // hash -> filenames[]
    const newFileHashes = new Map<string, string>() // filename -> hash
    const duplicates = new Map<string, string[]>() // hash -> filenames[]

    // Calculate hashes for all files
    for (const file of files) {
      try {
        const hash = await calculateFileHash(file)
        newFileHashes.set(file.name, hash)
        
        if (!hashMap.has(hash)) {
          hashMap.set(hash, [])
        }
        hashMap.get(hash)!.push(file.name)
      } catch (error) {
        console.error(`Error calculating hash for ${file.name}:`, error)
      }
    }

    // Find duplicates (hashes with more than one file)
    for (const [hash, filenames] of hashMap.entries()) {
      if (filenames.length > 1) {
        duplicates.set(hash, filenames)
      }
    }

    setFileHashes(newFileHashes)
    setDuplicateFilesInList(duplicates)

    return duplicates
  }

  const loadTeams = async () => {
    try {
      const response = await teamsApi.getAll()
      setAvailableTeams(response.data.results || [])
    } catch (error) {
      console.error('Error loading teams:', error)
    }
  }

  const checkAiAvailability = async () => {
    try {
      const response = await uploadsApi.checkAiAvailability()
      setAiAvailable(response.data.ai_available)
      setAiMessage(response.data.message)
      setExternalConsentRequired(Boolean(response.data.external_cv_ai_consent_required))
      setCanGrantExternalConsent(Boolean(response.data.can_grant_external_cv_ai))
    } catch (error) {
      console.error('Error checking AI availability:', error)
      setAiAvailable(false)
      setAiMessage(error.response?.data?.message || t('candidates.aiCheckFailed'))
    }
  }

  const handleGrantExternalConsent = async () => {
    setGrantingConsent(true)
    try {
      await uploadsApi.grantExternalCvAiConsent()
      setExternalConsentRequired(false)
    } catch (error: any) {
      addNotification({
        type: 'alert',
        title: t('common.error'),
        message: error.response?.data?.error || t('candidates.externalCvAiConsent.failed', 'Could not save your consent. Please try again.'),
        priority: 'high',
        category: 'cv'
      })
    } finally {
      setGrantingConsent(false)
    }
  }

  const processFiles = useCallback((files: File[]) => {
    const validExtensions = [
      '.pdf', '.docx', '.doc', '.txt',
      '.zip', '.rar', '.7z',
      '.tar', '.tar.gz', '.tgz',
      '.tar.bz2', '.tbz2',
      '.tar.xz', '.txz'
    ]

    const validFiles = files.filter(file =>
      validExtensions.some(ext => file.name.toLowerCase().endsWith(ext))
    )

    if (validFiles.length !== files.length) {
      addNotification({
        type: 'reminder',
        title: t('common.warning'),
        message: t('candidates.massImportModal.invalidFilesWarning'),
        titleKey: 'common.warning',
        messageKey: 'candidates.massImportModal.invalidFilesWarning',
        priority: 'medium',
        category: 'cv'
      })
    }

    const updatedFiles = [...selectedFiles, ...validFiles]
    setSelectedFiles(updatedFiles)
    
    // Check for duplicates in the background (non-blocking)
    // Skip duplicate check for large batches (>50 files) to avoid blocking
    if (updatedFiles.length <= 50) {
      checkDuplicatesInList(updatedFiles).then(duplicates => {
        if (duplicates.size > 0) {
          const duplicateCount = Array.from(duplicates.values()).reduce((sum, files) => sum + files.length, 0) - duplicates.size
          const duplicateGroups = Array.from(duplicates.values())
          const fileList = duplicateGroups.map(group => group.join(', ')).join('; ')
          
          addNotification({
            type: 'reminder',
            title: t('candidates.massImportModal.duplicatesInListTitle', 'Duplicate Files Detected'),
            message: t('candidates.massImportModal.duplicatesInListMessage', {
              count: duplicateCount,
              files: fileList
            }),
            titleKey: 'candidates.massImportModal.duplicatesInListTitle',
            messageKey: 'candidates.massImportModal.duplicatesInListMessage',
            messageParams: { count: duplicateCount, files: fileList },
            priority: 'medium',
            category: 'cv'
          })
        }
      }).catch(error => {
        console.error('Error checking duplicates:', error)
        // Don't block file selection if duplicate check fails
      })
    } else {
      // For large batches, clear duplicate state and let backend handle it
      setDuplicateFilesInList(new Map())
      setFileHashes(new Map())
    }
  }, [addNotification, t, selectedFiles])


  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    if (acceptedFiles.length > 0) {
      processFiles(acceptedFiles)
    }
    if (rejectedFiles.length > 0) {
      addNotification({
        type: 'reminder',
        title: t('common.warning'),
        message: t('candidates.massImportModal.invalidFilesWarning'),
        titleKey: 'common.warning',
        messageKey: 'candidates.massImportModal.invalidFilesWarning',
        priority: 'medium',
        category: 'cv'
      })
    }
  }, [processFiles, addNotification, t])

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    multiple: true,
    disabled: isUploading || isStaging || aiAvailable === false,
    noClick: false, // Allow clicking on the dropzone area to open file dialog
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
      'text/plain': ['.txt'],
      'application/zip': ['.zip'],
      'application/x-rar-compressed': ['.rar'],
      'application/x-7z-compressed': ['.7z'],
      'application/x-tar': ['.tar'],
      'application/gzip': ['.tar.gz', '.tgz'],
      'application/x-bzip2': ['.tar.bz2', '.tbz2'],
      'application/x-xz': ['.tar.xz', '.txz']
    }
  })


  const removeFile = (index: number) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index)
    setSelectedFiles(updatedFiles)
    
    // Recheck duplicates after removal (non-blocking, only for small batches)
    if (updatedFiles.length > 0 && updatedFiles.length <= 50) {
      checkDuplicatesInList(updatedFiles).catch(error => {
        console.error('Error checking duplicates after removal:', error)
      })
    } else {
      setDuplicateFilesInList(new Map())
      setFileHashes(new Map())
    }
  }

  const startStatusPolling = (batchId: string) => {
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
    }
    if (pollingInterval) {
      clearInterval(pollingInterval)
    }

    setTimeout(() => {
      setIsUploading(false)
    }, 600)

    // Use global polling service that persists across component unmounts
    batchPollingService.startPolling(
      batchId,
      async (id) => await uploadsApi.getBatchStatus(id),
      (statusData) => {
        // Status update callback - called every poll
        setBatchStatus(statusData)
        setPendingBatchSummary(null)
        
        // Calculate skipped files count
        const skippedCount = statusData.skipped_files?.length || 0
        
        // Update status message with batch details (pass batchId to track multiple batches)
        setStatusMessage(
          statusData.status === 'paused'
            ? t('candidates.massImportModal.importPausedMessage')
            : t('candidates.massImportModal.statusMessageInFlight', {
                processed: statusData.processed_files,
                total: statusData.total_files
              }),
          {
            total: statusData.total_files,
            processed: statusData.processed_files,
            successful: statusData.successful_files || 0,
            failed: statusData.failed_files || 0,
            skipped: skippedCount
          },
          batchId // Pass batchId to track this specific batch
        )
      },
      () => {
        // Complete callback - called when batch is done
        // Get final status before cleaning up
        uploadsApi.getBatchStatus(batchId).then((response) => {
          const finalStatus = response.data
          setBatchStatus(finalStatus)
          
          // Remove this batch from active batches first
          removeBatch(batchId)
          
          pollingIntervalRef.current = null
          setPollingInterval(null)
          activeBatchIdRef.current = null
          setActiveBatchId(null)

          if (finalStatus.status === 'completed' || finalStatus.status === 'partially_completed') {
            finishProcessing({ showDoneMessage: true })
            setStatusMessage(
              t('candidates.massImportModal.statusMessageCompleted', {
                total: finalStatus.total_files
              })
            )
            addNotification({
              type: 'update',
              title: t('candidates.massImportModal.processingComplete'),
              message: t('candidates.massImportModal.processingCompleteMessage', {
                success: finalStatus.successful_files,
                total: finalStatus.total_files
              }),
              title_key: 'candidates.massImportModal.processingComplete',
              message_key: 'candidates.massImportModal.processingCompleteMessage',
              message_params: { success: finalStatus.successful_files, total: finalStatus.total_files },
              priority: 'medium',
              category: 'cv',
              batchId: batchId // Store only batchId, fetch details from API when needed
            })
            onImportComplete?.()
          } else if (finalStatus.status === 'cancelled') {
            if (!userCancelledRef.current) {
              finishProcessing()
              addNotification({
                type: 'system',
                title: t('candidates.massImportModal.batchCancelled'),
                message: t('candidates.massImportModal.batchCancelledMessage'),
                titleKey: 'candidates.massImportModal.batchCancelled',
                messageKey: 'candidates.massImportModal.batchCancelledMessage',
                priority: 'low',
                category: 'cv'
              })
            }
          } else {
            reportError(t('candidates.massImportModal.statusMessageFailed'))
            addNotification({
              type: 'alert',
              title: t('candidates.massImportModal.processingFailed'),
              message: t('candidates.massImportModal.processingFailedMessage'),
              title_key: 'candidates.massImportModal.processingFailed',
              message_key: 'candidates.massImportModal.processingFailedMessage',
              priority: 'high',
              category: 'cv',
              batchId: batchId // Store only batchId, fetch details from API when needed
            })
          }
        }).catch((error) => {
          console.error('Error getting final batch status:', error)
          finishProcessing()
        })
      },
      2000 // Poll interval
    )

    // Also keep local interval for UI updates when modal is open
    const interval = setInterval(async () => {
      try {
        const response = await uploadsApi.getBatchStatus(batchId)
        setBatchStatus(response.data)
        setPendingBatchSummary(null)
        setStatusMessage(
          response.data.status === 'paused'
            ? t('candidates.massImportModal.importPausedMessage')
            : t('candidates.massImportModal.statusMessageInFlight', {
                processed: response.data.processed_files,
                total: response.data.total_files
              })
        )

        if (['completed', 'failed', 'cancelled', 'partially_completed'].includes(response.data.status)) {
          clearInterval(interval)
          pollingIntervalRef.current = null
          setPollingInterval(null)
          activeBatchIdRef.current = null
          setActiveBatchId(null)
        }
      } catch (error) {
        console.error('Error polling batch status:', error)
      }
    }, 2000)

    pollingIntervalRef.current = interval
    setPollingInterval(interval)
  }

  const handleCancelBatch = async () => {
    if (!batchStatus?.batch_id) return

    try {
      userCancelledRef.current = true
      await uploadsApi.cancelBatch(batchStatus.batch_id)
      // Stop global polling service and remove batch
      if (activeBatchIdRef.current) {
        batchPollingService.stopPolling(activeBatchIdRef.current)
        removeBatch(activeBatchIdRef.current)
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
      if (pollingInterval) {
        clearInterval(pollingInterval)
        setPollingInterval(null)
      }
      activeBatchIdRef.current = null
      setActiveBatchId(null)
      setBatchStatus(null)
      finishProcessing()
      addNotification({
        type: 'system',
        title: t('candidates.massImportModal.batchCancelled'),
        message: t('candidates.massImportModal.batchCancelledMessage'),
        titleKey: 'candidates.massImportModal.batchCancelled',
        messageKey: 'candidates.massImportModal.batchCancelledMessage',
        priority: 'low',
        category: 'cv'
      })
    } catch (error) {
      userCancelledRef.current = false
      console.error('Error cancelling batch:', error)
    }
  }

  const beginBatchTracking = (summary: {
    batch_id: string
    batch_name: string
    total_files: number
    task_id?: string
  }) => {
    const optimisticStatus: BatchStatus = {
      batch_id: summary.batch_id,
      batch_name: summary.batch_name,
      status: 'processing',
      progress_percentage: 0,
      total_files: summary.total_files,
      processed_files: 0,
      successful_files: 0,
      failed_files: 0,
      created_at: '',
      started_at: '',
      completed_at: '',
      task_id: summary.task_id || '',
      files: []
    }

    setBatchStatus(optimisticStatus)
    setPendingBatchSummary({
      batchId: summary.batch_id,
      batchName: summary.batch_name,
      totalFiles: summary.total_files
    })
    startProcessing(
      summary.batch_name ||
        t('candidates.massImportModal.batchProcessingLabel', { count: summary.total_files })
    )
    setStatusMessage(
      t('candidates.massImportModal.statusMessageQueued', { count: summary.total_files })
    )
    userCancelledRef.current = false
    setActiveBatchId(summary.batch_id)
    activeBatchIdRef.current = summary.batch_id
    startStatusPolling(summary.batch_id)
  }

  const handleStageFiles = async () => {
    if (selectedFiles.length === 0) {
      addNotification({
        type: 'alert',
        title: t('common.error'),
        message: t('candidates.massImportModal.noFilesSelected'),
        titleKey: 'common.error',
        messageKey: 'candidates.massImportModal.noFilesSelected',
        priority: 'high',
        category: 'cv'
      })
      return
    }

    if (aiAvailable === false) {
      addNotification({
        type: 'alert',
        title: t('common.error'),
        message: t('candidates.aiEngineNotAvailable'),
        titleKey: 'common.error',
        messageKey: 'candidates.aiEngineNotAvailable',
        priority: 'high',
        category: 'cv'
      })
      return
    }

    setIsStaging(true)
    try {
      const formData = new FormData()
      selectedFiles.forEach(file => {
        formData.append('files', file)
      })
      const now = new Date()
      const defaultBatchName = `${t('candidates.massImportModal.cvImport')} ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`
      formData.append('batch_name', defaultBatchName)
      if (selectedTeam) {
        formData.append('team_id', selectedTeam)
      }
      formData.append('platform_language', i18n.language)

      const response = await uploadsApi.stageBatch(formData)
      setReviewBatchId(response.data.batch_id)
      setStagedFiles(response.data.files || [])
      setLocalStep('review')
      addNotification({
        type: 'update',
        title: t('candidates.massImportModal.screeningDone'),
        message: t('candidates.massImportModal.screeningDoneMessage', {
          ready: response.data.ready_count,
          duplicates: response.data.duplicate_count,
          errors: response.data.error_count
        }),
        titleKey: 'candidates.massImportModal.screeningDone',
        messageKey: 'candidates.massImportModal.screeningDoneMessage',
        messageParams: {
          ready: response.data.ready_count,
          duplicates: response.data.duplicate_count,
          errors: response.data.error_count
        },
        priority: 'medium',
        category: 'cv'
      })
    } catch (error: any) {
      if (error.response?.status === 400 && error.response?.data?.error === 'too_many_files') {
        const maxFiles = error.response.data.max_files || 100
        addNotification({
          type: 'alert',
          title: t('candidates.massImportModal.tooManyFilesTitle', 'Too Many Files'),
          message: error.response.data.message || t('candidates.massImportModal.tooManyFilesMessage', { max: maxFiles }),
          titleKey: 'candidates.massImportModal.tooManyFilesTitle',
          messageKey: error.response.data.message ? undefined : 'candidates.massImportModal.tooManyFilesMessage',
          messageParams: { max: maxFiles },
          priority: 'high',
          category: 'cv'
        })
      } else {
        addNotification({
          type: 'alert',
          title: t('candidates.massImportModal.uploadFailed'),
          message: error.response?.data?.message || error.response?.data?.error || t('candidates.massImportModal.uploadFailedMessage'),
          titleKey: 'candidates.massImportModal.uploadFailed',
          messageKey: error.response?.data?.message || error.response?.data?.error ? undefined : 'candidates.massImportModal.uploadFailedMessage',
          priority: 'high',
          category: 'cv'
        })
      }
    } finally {
      setIsStaging(false)
    }
  }

  const handleRemoveStagedFile = async (file: StagedFile, index: number) => {
    if (file.status === 'ready' && file.cv_upload_id && reviewBatchId) {
      try {
        await uploadsApi.deleteStagedFile(reviewBatchId, file.cv_upload_id)
      } catch (error) {
        console.error('Error removing staged file:', error)
        addNotification({
          type: 'alert',
          title: t('common.error'),
          message: t('candidates.massImportModal.removeFileFailed'),
          titleKey: 'common.error',
          messageKey: 'candidates.massImportModal.removeFileFailed',
          priority: 'high',
          category: 'cv'
        })
        return
      }
    }
    setStagedFiles(prev => prev.filter((_, fileIndex) => fileIndex !== index))
  }

  const handleStartImport = async () => {
    const readyCount = stagedFiles.filter(file => file.status === 'ready').length
    if (!reviewBatchId || readyCount === 0) {
      addNotification({
        type: 'alert',
        title: t('common.error'),
        message: t('candidates.massImportModal.noFilesToImport'),
        titleKey: 'common.error',
        messageKey: 'candidates.massImportModal.noFilesToImport',
        priority: 'high',
        category: 'cv'
      })
      return
    }

    setIsStartingImport(true)
    try {
      const response = await uploadsApi.startStagedBatch(reviewBatchId, selectedTeam || null)
      setReviewBatchId(null)
      setStagedFiles([])
      setLocalStep('select')
      setSelectedFiles([])
      beginBatchTracking(response.data)
    } catch (error: any) {
      addNotification({
        type: 'alert',
        title: t('candidates.massImportModal.uploadFailed'),
        message: error.response?.data?.message || error.response?.data?.error || t('candidates.massImportModal.uploadFailedMessage'),
        titleKey: 'candidates.massImportModal.uploadFailed',
        messageKey: error.response?.data?.message || error.response?.data?.error ? undefined : 'candidates.massImportModal.uploadFailedMessage',
        priority: 'high',
        category: 'cv'
      })
    } finally {
      setIsStartingImport(false)
    }
  }

  const handlePauseBatch = async () => {
    if (!batchStatus?.batch_id) return
    setIsControllingBatch(true)
    try {
      await uploadsApi.pauseBatch(batchStatus.batch_id)
      setBatchStatus(prev => prev ? { ...prev, status: 'paused' } : prev)
      addNotification({
        type: 'system',
        title: t('candidates.massImportModal.importPaused'),
        message: t('candidates.massImportModal.importPausedMessage'),
        titleKey: 'candidates.massImportModal.importPaused',
        messageKey: 'candidates.massImportModal.importPausedMessage',
        priority: 'low',
        category: 'cv'
      })
    } catch (error) {
      console.error('Error pausing batch:', error)
    } finally {
      setIsControllingBatch(false)
    }
  }

  const handleResumeBatch = async () => {
    if (!batchStatus?.batch_id) return
    setIsControllingBatch(true)
    try {
      await uploadsApi.resumeBatch(batchStatus.batch_id)
      setBatchStatus(prev => prev ? { ...prev, status: 'processing' } : prev)
      addNotification({
        type: 'system',
        title: t('candidates.massImportModal.importResumed'),
        message: t('candidates.massImportModal.importResumedMessage'),
        titleKey: 'candidates.massImportModal.importResumed',
        messageKey: 'candidates.massImportModal.importResumedMessage',
        priority: 'low',
        category: 'cv'
      })
    } catch (error) {
      console.error('Error resuming batch:', error)
    } finally {
      setIsControllingBatch(false)
    }
  }

  const handleBackToSelection = () => {
    if (reviewBatchId) {
      uploadsApi.discardStagedBatch(reviewBatchId).catch((error) => {
        console.error('Error discarding screened batch:', error)
      })
    }
    setReviewBatchId(null)
    setStagedFiles([])
    setLocalStep('select')
  }

  const resetModal = () => {
    const importStillRunning = Boolean(activeBatchIdRef.current)
    setSelectedFiles([])
    if (!importStillRunning) {
      setSelectedTeam('')
      setBatchStatus(null)
      setPendingBatchSummary(null)
    }
    setShowInfoTooltip(false)
    setDuplicateError(null)
    setFileHashes(new Map())
    setDuplicateFilesInList(new Map())
    setLocalStep('select')
    setStagedFiles([])
    setReviewBatchId(null)
    setIsStaging(false)
    setIsStartingImport(false)
    // Don't clear polling if batch is still active - let it continue in background
    // Only clear if explicitly resetting (e.g., after completion)
    if (pollingInterval && !importStillRunning) {
      clearInterval(pollingInterval)
      setPollingInterval(null)
    }
  }

  const handleClose = () => {
    if (reviewBatchId && !activeBatchIdRef.current) {
      uploadsApi.discardStagedBatch(reviewBatchId).catch((error) => {
        console.error('Error discarding screened batch:', error)
      })
    }
    resetModal()
    onClose()
  }

  const handleCloseAndReload = () => {
    resetModal()
    onClose()
    window.location.reload()
  }

  const stagedFileLabel = (file: StagedFile) => {
    if (file.status === 'duplicate') {
      if (file.reason === 'duplicate_in_batch') {
        return t('candidates.massImportModal.duplicateInList')
      }
      if (file.duplicate_candidate_name) {
        return `${t('candidates.massImportModal.duplicateFileMessage')} (${t('candidates.massImportModal.alreadyExistsFor')}: ${file.duplicate_candidate_name})`
      }
      return t('candidates.massImportModal.duplicateFileMessage')
    }
    if (file.reason === 'too_large') return t('candidates.massImportModal.fileTooLarge')
    if (file.reason === 'unsupported') return t('candidates.massImportModal.fileUnsupported')
    if (file.reason === 'invalid_archive') return t('candidates.massImportModal.fileInvalidArchive')
    if (file.reason === 'empty') return t('candidates.massImportModal.fileEmpty')
    if (file.status === 'ready') return t('candidates.massImportModal.fileReady')
    return file.message || t('candidates.massImportModal.fileInvalid')
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
  }

  const getStatusIcon = (status: string) => {
    const baseClasses = "h-5 w-5"
    let icon, tooltip
    
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'processed':  // Backend uses 'processed' status
        icon = <CheckCircle className={`${baseClasses} text-green-500`} />
        tooltip = t('candidates.massImportModal.statusCompleted')
        break
      case 'failed':
        icon = <XCircle className={`${baseClasses} text-red-500`} />
        tooltip = t('candidates.massImportModal.statusFailed')
        break
      case 'processing':
        icon = <Loader2 className={`${baseClasses} text-blue-500 animate-spin`} />
        tooltip = t('candidates.massImportModal.statusProcessing')
        break
      case 'paused':
        icon = <Pause className={`${baseClasses} text-amber-500`} />
        tooltip = t('candidates.massImportModal.statusPaused')
        break
      case 'cancelled':
        icon = <XCircle className={`${baseClasses} text-muted-foreground`} />
        tooltip = t('candidates.massImportModal.statusCancelled')
        break
      case 'duplicate':
      case 'duplicated':
        icon = <Copy className={`${baseClasses} text-orange-500`} />
        tooltip = t('candidates.massImportModal.statusDuplicate', 'Duplicate')
        break
      case 'uploaded':
        icon = <FileText className={`${baseClasses} text-gray-500`} />
        tooltip = t('candidates.massImportModal.statusUploaded')
        break
      default:
        icon = <AlertCircle className={`${baseClasses} text-yellow-500`} />
        tooltip = status ? t('candidates.massImportModal.statusUnknown', { status }) : t('candidates.massImportModal.statusUnknown', { status: 'Unknown' })
    }

  return (
      <div className="flex-shrink-0" title={tooltip}>
        {icon}
      </div>
    )
  }

  const hasActiveBatch = Boolean(batchStatus) || Boolean(pendingBatchSummary) || isUploading

  const renderContent = () => {
    if (!canMassImport) {
      return (
        <div className="rounded-lg border border-dashed border-muted-foreground/40 p-6 text-center text-sm text-muted-foreground">
          {t('candidates.addModal.notAvailable')}
        </div>
      )
    }

    if (externalConsentRequired && !hasActiveBatch) {
      return (
        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                {t('candidates.externalCvAiConsent.title', 'Allow sending CVs to external AI?')}
              </h3>
              <p className="text-sm text-amber-700 dark:text-amber-200/90">
                {t(
                  'candidates.externalCvAiConsent.message',
                  'Your organization uses an external AI provider (e.g. Ollama Cloud, OpenRouter). To analyze CVs, candidate personal data will be sent to this provider.'
                )}
              </p>
              {canGrantExternalConsent ? (
                <p className="text-xs text-amber-700 dark:text-amber-200/80">
                  {t(
                    'candidates.externalCvAiConsent.confirm',
                    'By allowing, you confirm this is permitted for your organization (e.g. contract / DPA in place). This is asked only once.'
                  )}
                </p>
              ) : (
                <p className="text-xs text-amber-700 dark:text-amber-200/80">
                  {t(
                    'candidates.externalCvAiConsent.askAdmin',
                    'Please ask your organization admin to allow it before uploading CVs.'
                  )}
                </p>
              )}
            </div>
          </div>
        </div>
      )
    }

    // Show duplicate warning state (candidates already exist, added to group)
    if (duplicateError?.allDuplicates) {
      return (
        <div className="space-y-4">
          <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                  {t('candidates.massImportModal.duplicateWarningTitle', 'Candidate(s) Already Exist')}
                </h3>
                <p className="text-sm text-amber-700 dark:text-amber-200/90">
                  {t('candidates.massImportModal.duplicateWarningMessage', 'These candidate(s) already exist in the candidates list. They have been added to the group.')}
                </p>
                {duplicateError.duplicateFiles.length > 0 && (
                  <div className="mt-3 space-y-1">
                    <p className="text-xs font-medium text-amber-800 dark:text-amber-200">
                      {t('candidates.massImportModal.duplicateFilesList', 'Duplicate files')}:
                    </p>
                    <ul className="text-xs text-amber-700 dark:text-amber-200/80 space-y-1 max-h-32 overflow-y-auto">
                      {duplicateError.duplicateFiles.slice(0, 10).map((file, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-amber-600 dark:text-amber-400">•</span>
                          <span className="flex-1">
                            {file.filename}
                            {file.duplicate_candidate_name ? (
                              <span className="ml-1 opacity-90">
                                ({t('candidates.massImportModal.alreadyExistsFor', 'Already exists for')}: {file.duplicate_candidate_name})
                              </span>
                            ) : (
                              <span className="ml-1 italic opacity-90">
                                ({t('candidates.massImportModal.alreadyUploaded', 'Already uploaded')})
                              </span>
                            )}
                          </span>
                        </li>
                      ))}
                      {duplicateError.duplicateFiles.length > 10 && (
                        <li className="opacity-90">
                          ...and {duplicateError.duplicateFiles.length - 10} more
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setDuplicateError(null)}>
              {t('candidates.massImportModal.selectDifferentFiles', 'Select Different Files')}
            </Button>
            <Button size="sm" onClick={handleCloseAndReload}>
              {t('candidates.massImportModal.closeAndReload', 'Close')}
            </Button>
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        {aiAvailable === false && (
          <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/30 px-3 py-2 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-xs font-semibold text-red-700 dark:text-red-200">
                {t('candidates.aiEngineNotAvailable')}
              </h3>
              <p className="text-xs text-red-600 dark:text-red-200/80 mt-0.5 leading-relaxed">{aiMessage}</p>
            </div>
          </div>
        )}

        {/* Tabs for Import Source */}
        {!hasActiveBatch && (
          <div className="flex border-b border-border">
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'local'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
              onClick={() => setActiveTab('local')}
            >
              {t('candidates.addModal.importLocal', 'Import local')}
            </button>
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'email'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
              onClick={() => setActiveTab('email')}
            >
              {t('candidates.addModal.importEmail', 'Import depuis Email')}
            </button>
          </div>
        )}

        {/* Info Icon with Tooltip */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-foreground">
              {t('candidates.massImportModal.headerTitle')}
            </h3>
            <div className="relative">
              <button
                ref={infoIconRef}
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  if (infoIconRef.current) {
                    const rect = infoIconRef.current.getBoundingClientRect()
                    setTooltipPosition({
                      top: rect.bottom + 8,
                      left: rect.left
                    })
                  }
                  setShowInfoTooltip(!showInfoTooltip)
                }}
                className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors flex-shrink-0"
                aria-label={t('candidates.massImportModal.info', 'Information')}
              >
                <Info className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Tooltip Portal */}
        {showInfoTooltip && tooltipPosition && ReactDOM.createPortal(
          <>
            {/* Backdrop to close on outside click */}
            <div
              className="fixed inset-0 z-[150]"
              onClick={() => setShowInfoTooltip(false)}
            />
            <div
              className="fixed z-[200] w-80 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/50 p-3 shadow-xl"
              style={{
                top: `${tooltipPosition.top}px`,
                left: `${tooltipPosition.left}px`,
                maxWidth: 'calc(100vw - 2rem)'
              }}
            >
              <div className="space-y-2">
                <p className="text-xs text-blue-900 dark:text-blue-100 leading-relaxed">
                  {t('candidates.massImportModal.headerSubtitle')}
                </p>
                <ul className="text-xs text-blue-900/80 dark:text-blue-200/80 list-disc pl-4 space-y-1">
                  <li>{t('candidates.massImportModal.supportedFormats')}</li>
                  <li>{t('candidates.massImportModal.maxSize')}</li>
                </ul>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setShowInfoTooltip(false)
                }}
                className="absolute top-2 right-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                aria-label={t('common.close', 'Close')}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          </>,
          document.body
        )}

        {!hasActiveBatch && activeTab === 'local' && (
          <div className="flex items-center gap-2 text-xs">
            <span className={localStep === 'select' ? 'font-semibold text-primary' : 'text-muted-foreground'}>
              {t('candidates.massImportModal.stepScreen')}
            </span>
            <span className="text-muted-foreground">/</span>
            <span className={localStep === 'review' ? 'font-semibold text-primary' : 'text-muted-foreground'}>
              {t('candidates.massImportModal.stepImport')}
            </span>
          </div>
        )}

        {!hasActiveBatch && activeTab === 'local' && localStep === 'select' && (
          <>
            <div
              {...getRootProps()}
              className={`rounded-lg border-2 border-dashed p-6 transition-colors ${
                isDragActive
                  ? 'border-primary bg-primary/5 cursor-pointer'
                  : 'border-border bg-muted/30'
              } ${isUploading || isStaging || aiAvailable === false ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <input {...getInputProps()} />
              <div className="flex flex-col items-center justify-center text-center space-y-3">
                <div className="rounded-full bg-muted p-3">
                  <Upload className={`h-6 w-6 ${isDragActive ? 'text-primary' : 'text-muted-foreground'}`} />
                </div>
                <div className="space-y-1.5">
                  <p className="text-sm font-semibold text-foreground">
                    {isDragActive
                      ? t('candidates.massImportModal.dropFilesHere', 'Drop files here')
                      : t('candidates.massImportModal.uploadFilesLabel')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {selectedFiles.length > 0
                      ? t('candidates.massImportModal.filesSelected', { count: selectedFiles.length })
                      : t('candidates.massImportModal.dropHint')}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isUploading || isStaging || aiAvailable === false}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    {t('candidates.massImportModal.selectFiles')}
                  </Button>
                  {selectedFiles.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedFiles([])
                      }}
                      disabled={isUploading}
                    >
                      <X className="mr-2 h-4 w-4" />
                      {t('candidates.massImportModal.clear')}
                    </Button>
                  )}
                </div>
              </div>
              {selectedFiles.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border space-y-1.5">
                  {selectedFiles.map((file, index) => {
                    const fileHash = fileHashes.get(file.name)
                    const isDuplicate = fileHash && Array.from(duplicateFilesInList.values()).some(files => files.includes(file.name))
                    const duplicateGroup = fileHash ? duplicateFilesInList.get(fileHash) : null
                    const hasOtherDuplicates = duplicateGroup && duplicateGroup.length > 1
                    
                    return (
                      <div 
                        key={`${file.name}-${index}`} 
                        className={`flex items-center justify-between rounded-lg border px-2.5 py-1.5 text-sm ${
                          isDuplicate 
                            ? 'border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-950/20' 
                            : 'border-border bg-background'
                        }`}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <FileText className={`h-3.5 w-3.5 flex-shrink-0 ${isDuplicate ? 'text-orange-500' : 'text-muted-foreground'}`} />
                          <div className="min-w-0 flex-1">
                            <p className={`font-medium text-xs ${isDuplicate ? 'text-orange-700 dark:text-orange-300' : 'text-foreground'}`}>
                              {file.name}
                            </p>
                            <div className="flex items-center gap-2">
                              <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                              {isDuplicate && hasOtherDuplicates && (
                                <span className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                                  ({t('candidates.massImportModal.duplicateInList', 'Duplicate in selection')})
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            removeFile(index)
                          }}
                          disabled={isUploading}
                          className="h-7 w-7 p-0 flex-shrink-0"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {!hasActiveBatch && activeTab === 'local' && localStep === 'review' && (
          <div className="space-y-3 rounded-lg border border-border bg-card p-3">
            <div>
              <p className="text-sm font-semibold text-foreground">
                {t('candidates.massImportModal.reviewTitle')}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t('candidates.massImportModal.reviewHint')}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div className="rounded-lg bg-green-50 dark:bg-green-950/30 p-2">
                <p className="text-base font-semibold text-green-600">
                  {stagedFiles.filter(file => file.status === 'ready').length}
                </p>
                <p className="text-muted-foreground">{t('candidates.massImportModal.readyShort')}</p>
              </div>
              <div className="rounded-lg bg-orange-50 dark:bg-orange-950/30 p-2">
                <p className="text-base font-semibold text-orange-600">
                  {stagedFiles.filter(file => file.status === 'duplicate').length}
                </p>
                <p className="text-muted-foreground">{t('candidates.massImportModal.duplicateShort')}</p>
              </div>
              <div className="rounded-lg bg-red-50 dark:bg-red-950/30 p-2">
                <p className="text-base font-semibold text-red-600">
                  {stagedFiles.filter(file => file.status === 'error').length}
                </p>
                <p className="text-muted-foreground">{t('candidates.massImportModal.errorShort')}</p>
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto rounded-lg border border-border divide-y divide-border">
              {stagedFiles.map((file, index) => {
                const isReady = file.status === 'ready'
                const isDuplicate = file.status === 'duplicate'
                return (
                  <div
                    key={`${file.filename}-${file.cv_upload_id ?? index}`}
                    className={`flex items-center justify-between gap-2 p-2 text-xs ${
                      isDuplicate
                        ? 'bg-orange-50 dark:bg-orange-950/20'
                        : isReady
                          ? 'bg-background'
                          : 'bg-red-50 dark:bg-red-950/20'
                    }`}
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <FileText className={`h-3.5 w-3.5 flex-shrink-0 ${
                        isDuplicate ? 'text-orange-500' : isReady ? 'text-green-600' : 'text-red-500'
                      }`} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-foreground">{file.filename}</p>
                        <p className={`truncate ${
                          isDuplicate ? 'text-orange-600 dark:text-orange-400' : isReady ? 'text-muted-foreground' : 'text-red-600 dark:text-red-400'
                        }`}>
                          {file.file_size ? `${formatFileSize(file.file_size)} · ` : ''}
                          {stagedFileLabel(file)}
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveStagedFile(file, index)}
                      className="h-7 w-7 p-0 flex-shrink-0"
                      aria-label={t('candidates.massImportModal.removeFile')}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )
              })}
            </div>
            {stagedFiles.filter(file => file.status === 'ready').length === 0 && (
              <p className="text-xs text-muted-foreground">
                {t('candidates.massImportModal.noFilesToImport')}
              </p>
            )}
          </div>
        )}

        {!hasActiveBatch && activeTab === 'email' && (
          <EmailImportView
            selectedTeam={selectedTeam}
            disabled={isUploading || aiAvailable === false}
            onImportStarted={(summary) => {
              const optimisticStatus: BatchStatus = {
                batch_id: summary.batchId,
                batch_name: summary.batchName,
                status: 'processing',
                progress_percentage: 0,
                total_files: summary.totalFiles,
                processed_files: 0,
                successful_files: 0,
                failed_files: 0,
                created_at: '',
                started_at: '',
                completed_at: '',
                task_id: '',
                files: []
              }
              setBatchStatus(optimisticStatus)
              setPendingBatchSummary(summary)
              
              startProcessing(summary.batchName)
              setStatusMessage(
                t('candidates.massImportModal.statusMessageQueued', { count: summary.totalFiles })
              )

              setActiveBatchId(summary.batchId)
              activeBatchIdRef.current = summary.batchId
              startStatusPolling(summary.batchId)
            }}
          />
        )}

        {!hasActiveBatch && (
          <div className="space-y-1.5 pt-2">
            <label className="block text-xs font-medium text-foreground">
              {lockTeamSelection
                ? t('candidates.massImportModal.teamLocked', 'Team pre-selected for this import')
                : t('candidates.massImportModal.teamOptional')}
            </label>
            <select
              value={selectedTeam}
              onChange={(e) => {
                if (lockTeamSelection) return
                setSelectedTeam(e.target.value)
              }}
              disabled={lockTeamSelection}
              className="flex h-9 w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-muted"
            >
              <option value="">{t('candidates.massImportModal.noTeamSelected')}</option>
              {availableTeams.map(team => (
                <option key={team.id} value={team.id.toString()}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {hasActiveBatch && (
          <div className="space-y-3 rounded-lg border border-border bg-card p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-2">
                {batchStatus ? getStatusIcon(batchStatus.status) : <Loader2 className="h-4 w-4 animate-spin text-blue-500" />}
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    {batchStatus?.batch_name || pendingBatchSummary?.batchName || t('candidates.massImportModal.processingUpload')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {batchStatus?.status === 'paused'
                      ? t('candidates.massImportModal.importPausedMessage')
                      : batchStatus?.status === 'cancelled'
                        ? t('candidates.massImportModal.batchCancelledMessage')
                        : batchStatus
                          ? `${t('candidates.massImportModal.progress')} · ${batchStatus.progress_percentage}%`
                          : pendingBatchSummary
                            ? t('candidates.massImportModal.uploadingFiles', { count: pendingBatchSummary.totalFiles })
                            : t('candidates.massImportModal.processingUpload')}
                  </p>
                </div>
              </div>
            </div>

            <Progress value={batchStatus ? batchStatus.progress_percentage : 10} />

            <div className="grid gap-2 text-sm md:grid-cols-5 sm:grid-cols-2">
              <div className="rounded-lg bg-muted/50 p-2 text-center">
                <p className="text-base font-semibold text-foreground">
                  {batchStatus?.total_files ?? pendingBatchSummary?.totalFiles ?? 0}
                </p>
                <p className="text-xs text-muted-foreground">{t('candidates.massImportModal.total')}</p>
              </div>
              <div className="rounded-lg bg-muted/50 p-2 text-center">
                <p className="text-base font-semibold text-green-600">{batchStatus?.successful_files ?? 0}</p>
                <p className="text-xs text-muted-foreground">{t('candidates.massImportModal.success')}</p>
              </div>
              <div className="rounded-lg bg-muted/50 p-2 text-center">
                <p className="text-base font-semibold text-blue-600">{batchStatus?.processed_files ?? 0}</p>
                <p className="text-xs text-muted-foreground">{t('candidates.massImportModal.processed')}</p>
              </div>
              <div className="rounded-lg bg-muted/50 p-2 text-center">
                <p className="text-base font-semibold text-red-500">{batchStatus?.failed_files ?? 0}</p>
                <p className="text-xs text-muted-foreground">{t('candidates.massImportModal.failed')}</p>
              </div>
              {batchStatus?.skipped_files && batchStatus.skipped_files.length > 0 && (
                <div className="rounded-lg bg-muted/50 p-2 text-center">
                  <p className="text-base font-semibold text-orange-500">{batchStatus.skipped_files.length}</p>
                  <p className="text-xs text-muted-foreground">Skipped</p>
                </div>
              )}
        </div>

            {(batchStatus && (batchStatus.files.length > 0 || (batchStatus.skipped_files && batchStatus.skipped_files.length > 0))) && (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-foreground">
                  {t('candidates.massImportModal.fileStatus')}
                </p>
                <div className="max-h-48 overflow-y-auto rounded-lg border border-border divide-y divide-border">
                  {/* Combine and sort all files by index for proper ordering */}
                  {(() => {
                    const allFiles: Array<{
                      type: 'processed' | 'skipped'
                      file_index: number
                      filename: string
                      status?: string
                      error_message?: string
                      message?: string
                      reason?: string
                      duplicate_candidate_name?: string
                      cv_upload_id?: number
                    }> = []
                    
                    // Add processed files
                    batchStatus.files.forEach(file => {
                      allFiles.push({
                        type: 'processed',
                        file_index: file.file_index,
                        filename: file.filename,
                        status: file.status,
                        error_message: file.error_message,
                        cv_upload_id: file.cv_upload_id
                      })
                    })
                    
                    // Add skipped files
                    if (batchStatus.skipped_files) {
                      batchStatus.skipped_files.forEach((skipped: any) => {
                        allFiles.push({
                          type: 'skipped',
                          file_index: skipped.file_index,
                          filename: skipped.filename,
                          message: skipped.message,
                          reason: skipped.reason,
                          duplicate_candidate_name: skipped.duplicate_candidate_name
                        })
                      })
                    }
                    
                    // Sort by file_index
                    allFiles.sort((a, b) => a.file_index - b.file_index)
                    
                    return allFiles.map((item, idx) => {
                      if (item.type === 'skipped') {
                        const skippedIsError = Boolean(item.reason && item.reason !== 'duplicate' && item.reason !== 'duplicate_in_batch')
                        return (
                          <div key={`skipped-${idx}`} className={`flex items-center justify-between gap-2 p-2 text-xs border-l-2 ${
                            skippedIsError
                              ? 'bg-red-50 dark:bg-red-950/20 border-red-500'
                              : 'bg-orange-50 dark:bg-orange-950/20 border-orange-500'
                          }`}>
                            <div className="flex min-w-0 flex-1 items-center gap-2">
                              <FileText className={`h-3.5 w-3.5 flex-shrink-0 ${skippedIsError ? 'text-red-500' : 'text-orange-500'}`} />
                              <div className="min-w-0 flex-1">
                                <p className="truncate font-medium text-foreground">{item.filename}</p>
                                <p className={`text-xs truncate ${skippedIsError ? 'text-red-600 dark:text-red-400' : 'text-orange-600 dark:text-orange-400'}`}>
                                  {skippedIsError
                                    ? (item.message || t('candidates.massImportModal.fileInvalid'))
                                    : t('candidates.massImportModal.duplicateFileMessage', 'This CV file has already been uploaded previously.')}
                                  {!skippedIsError && item.duplicate_candidate_name && (
                                    <span className="ml-1">
                                      ({t('candidates.massImportModal.alreadyExistsFor', 'Already exists for')}: {item.duplicate_candidate_name})
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                            {getStatusIcon(skippedIsError ? 'failed' : 'duplicate')}
                          </div>
                        )
                      } else {
                        const isFailed = item.status === 'failed'
                        return (
                          <div 
                            key={item.file_index} 
                            className={`flex items-center justify-between gap-2 p-2 text-xs ${
                              isFailed ? 'bg-red-50 dark:bg-red-950/20 border-l-2 border-red-500' : ''
                            }`}
                          >
                            <div className="flex min-w-0 flex-1 items-center gap-2">
                              <FileText className={`h-3.5 w-3.5 flex-shrink-0 ${isFailed ? 'text-red-500' : 'text-muted-foreground'}`} />
                              <div className="min-w-0 flex-1">
                                <p className="truncate font-medium text-foreground">{item.filename}</p>
                                {item.error_message && (
                                  <p className="text-xs text-red-600 dark:text-red-400 mt-0.5 break-words" title={item.error_message}>
                                    {item.error_message}
                                  </p>
                                )}
                                {!item.error_message && isFailed && (
                                  <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                                    Processing failed. Please try again or contact support.
                                  </p>
                                )}
                              </div>
                            </div>
                            {getStatusIcon(item.status || '')}
                          </div>
                        )
                      }
                    })
                  })()}
                </div>
              </div>
            )}
          </div>
        )}

        {hasActiveBatch && (
          <div className="flex flex-col gap-2">
            <p className="text-xs text-muted-foreground">
              {t('candidates.massImportModal.backgroundInfo', 'You can close this window. Processing continues in the background.')}
            </p>
          </div>
        )}
      </div>
    )
  }

  const renderFooter = () => {
    if (!canMassImport) return null

    if (externalConsentRequired && !hasActiveBatch) {
      return (
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={handleClose}>
            {t('candidates.massImportModal.cancel')}
          </Button>
          {canGrantExternalConsent && (
            <Button size="sm" onClick={handleGrantExternalConsent} disabled={grantingConsent}>
              {grantingConsent && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('candidates.externalCvAiConsent.allow', 'Allow and continue')}
            </Button>
          )}
        </div>
      )
    }

    if (!hasActiveBatch) {
      if (activeTab === 'email') {
        return (
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={handleClose}>
              {t('candidates.massImportModal.close')}
            </Button>
          </div>
        )
      }

      if (localStep === 'review') {
        const readyCount = stagedFiles.filter(file => file.status === 'ready').length
        return (
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={handleBackToSelection} disabled={isStartingImport}>
              {t('candidates.massImportModal.backToFiles')}
            </Button>
            <Button
              size="sm"
              onClick={handleStartImport}
              disabled={readyCount === 0 || isStartingImport || !reviewBatchId}
            >
              {isStartingImport ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('candidates.massImportModal.startingImport')}
                </>
              ) : (
                t('candidates.massImportModal.startImport')
              )}
            </Button>
          </div>
        )
      }

      return (
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={handleClose}>
            {t('candidates.massImportModal.cancel')}
          </Button>
          <Button
            size="sm"
            onClick={handleStageFiles}
            disabled={selectedFiles.length === 0 || aiAvailable === false || isStaging}
          >
            {isStaging ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('candidates.massImportModal.screening')}
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                {t('candidates.massImportModal.uploadFiles', { count: selectedFiles.length })}
              </>
            )}
          </Button>
        </div>
      )
    }

    const importIsActive = Boolean(
      batchStatus && ['pending', 'processing', 'paused'].includes(batchStatus.status)
    ) || (!batchStatus && Boolean(pendingBatchSummary))

    return (
      <div className="flex flex-wrap justify-end gap-2">
        {batchStatus?.status === 'paused' ? (
          <Button size="sm" onClick={handleResumeBatch} disabled={isControllingBatch}>
            <Play className="mr-2 h-4 w-4" />
            {t('candidates.massImportModal.resumeImport')}
          </Button>
        ) : importIsActive ? (
          <Button variant="outline" size="sm" onClick={handlePauseBatch} disabled={isControllingBatch || !batchStatus}>
            <Pause className="mr-2 h-4 w-4" />
            {t('candidates.massImportModal.pauseImport')}
          </Button>
        ) : null}
        {importIsActive && (
          <Button variant="outline" size="sm" onClick={handleCancelBatch} disabled={isControllingBatch}>
            {t('candidates.massImportModal.cancelImport')}
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={handleClose}>
          {t('candidates.massImportModal.close')}
        </Button>
      </div>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('candidates.addModal.title')}
      size="lg"
      footer={renderFooter()}
    >
      {renderContent()}
    </Modal>
  )
}

